import type {
  AccountInput,
  ActivityInput,
  AssetInput,
  BattlePlanInput,
  BattlePlanItemInput,
  CategoryInput,
  CommodityInput,
  EventInput,
  TagInput,
  EventTypeInput,
  LongTermGoalInput,
  MetricInput,
  PersonInput,
  PlanInput,
  PointInput,
  ScheduleInput,
  TaskInput,
  TimelineInput,
  TransactionInput,
} from "@/types";
import { Assistant8Client } from "@/api/client";
import { AuthSession } from "@/auth/authSession";
import { SyncFeed } from "@/sync/syncFeed";
import {
  ACCOUNT_FIELDS,
  ACTIVITY_FIELDS,
  ASSET_FIELDS,
  CATEGORY_FIELDS,
  COMMODITY_FIELDS,
  TAG_FIELDS,
  ENTITY_CATEGORY,
  ENTITY_COMMODITY,
  ENTITY_TAG,
  ENTITY_ASSET,
  ENTITY_SCHEDULE,
  ENTITY_EVENT_TYPE,
  ENTITY_BATTLE_PLAN,
  ENTITY_BATTLE_PLAN_ITEM,
  BATTLE_PLAN_FIELDS,
  BATTLE_PLAN_ITEM_FIELDS,
  EVENT_TYPE_FIELDS,
  SCHEDULE_FIELDS,
  ENTITY_ACCOUNT,
  ENTITY_ACTIVITY,
  ENTITY_EVENT,
  ENTITY_LONG_TERM_GOAL,
  ENTITY_METRIC,
  ENTITY_PERSON,
  ENTITY_PLAN,
  ENTITY_POINT,
  ENTITY_TASK,
  ENTITY_TIMELINE,
  ENTITY_TRANSACTION,
  EVENT_FIELDS,
  LONG_TERM_GOAL_FIELDS,
  METRIC_FIELDS,
  PERSON_FIELDS,
  PLAN_FIELDS,
  POINT_FIELDS,
  TASK_FIELDS,
  TIMELINE_FIELDS,
  TRANSACTION_FIELDS,
} from "@/sync/entities";

const baseUrl = import.meta.env.VITE_API_BASE_URL ?? "https://api.assistant8.com";

// One token-lifecycle owner for the whole app (see authSession.ts). Both the REST
// client and the realtime WebSocket refresh through this, sharing a single
// in-flight refresh so the rotating refresh token is never replayed concurrently.
export const authSession = new AuthSession(baseUrl);

export const client = new Assistant8Client({ baseUrl, tokens: authSession });

// One feed for the whole app (cursor + clock + pull loop); each collection is a
// read/write view over its own entity type within that single feed.
export const syncFeed = new SyncFeed(client);

export const activityStore = syncFeed.collectionFor<ActivityInput>(
  ENTITY_ACTIVITY,
  ACTIVITY_FIELDS,
);

export const goalStore = syncFeed.collectionFor<LongTermGoalInput>(
  ENTITY_LONG_TERM_GOAL,
  LONG_TERM_GOAL_FIELDS,
);

export const planStore = syncFeed.collectionFor<PlanInput>(ENTITY_PLAN, PLAN_FIELDS);

export const taskStore = syncFeed.collectionFor<TaskInput>(ENTITY_TASK, TASK_FIELDS);

export const metricStore = syncFeed.collectionFor<MetricInput>(ENTITY_METRIC, METRIC_FIELDS);

export const pointStore = syncFeed.collectionFor<PointInput>(ENTITY_POINT, POINT_FIELDS);

export const timelineStore = syncFeed.collectionFor<TimelineInput>(
  ENTITY_TIMELINE,
  TIMELINE_FIELDS,
);

export const eventStore = syncFeed.collectionFor<EventInput>(ENTITY_EVENT, EVENT_FIELDS);

export const personStore = syncFeed.collectionFor<PersonInput>(ENTITY_PERSON, PERSON_FIELDS);

export const commodityStore = syncFeed.collectionFor<CommodityInput>(
  ENTITY_COMMODITY,
  COMMODITY_FIELDS,
);

export const categoryStore = syncFeed.collectionFor<CategoryInput>(
  ENTITY_CATEGORY,
  CATEGORY_FIELDS,
);

export const tagStore = syncFeed.collectionFor<TagInput>(ENTITY_TAG, TAG_FIELDS);

export const accountStore = syncFeed.collectionFor<AccountInput>(ENTITY_ACCOUNT, ACCOUNT_FIELDS);

export const transactionStore = syncFeed.collectionFor<TransactionInput>(
  ENTITY_TRANSACTION,
  TRANSACTION_FIELDS,
);

export const assetStore = syncFeed.collectionFor<AssetInput>(ENTITY_ASSET, ASSET_FIELDS);

export const scheduleStore = syncFeed.collectionFor<ScheduleInput>(
  ENTITY_SCHEDULE,
  SCHEDULE_FIELDS,
);

export const eventTypeStore = syncFeed.collectionFor<EventTypeInput>(
  ENTITY_EVENT_TYPE,
  EVENT_TYPE_FIELDS,
);

export const battlePlanStore = syncFeed.collectionFor<BattlePlanInput>(
  ENTITY_BATTLE_PLAN,
  BATTLE_PLAN_FIELDS,
);

export const battlePlanItemStore = syncFeed.collectionFor<BattlePlanItemInput>(
  ENTITY_BATTLE_PLAN_ITEM,
  BATTLE_PLAN_ITEM_FIELDS,
);
